import clsx from 'clsx'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import React, { useContext, useEffect, useState } from 'react'

import ChangelogLinks from '@/components/changelog/ChangelogLinks'
import ChangelogLoader from '@/components/changelog/ChangelogLoader'
import ChangelogReactions from '@/components/changelog/ChangelogReactions'
import UserChangelogLabels from '@/components/changelog/UserChangelogLabels'
import UserPostItem from '@/components/posts/UserPostItem'
import PageTitle from '@/components/shared/common/PageTitle'
import HTMLViewer from '@/components/shared/components/HTMLViewer'
import UserAvatar from '@/components/shared/components/UserAvatar'
import LineHeader from '@/components/shared/ui/LineHeader'
import Tooltip from '@/components/shared/ui/Tooltip'
import { useTranslations } from '@/hooks/useTranslations'
import { formatJSONTimestamp, relativeDate } from '@/lib/helpers/dateHelpers'
import { userSideChangelogSinglePath } from '@/lib/helpers/pathHelpers'
import { getChangelogSingle } from '@/models/Changelog'
import type { IChangelog } from '@/types/changelog'
import type { IUserProfile } from '@/types/user'

import HNContext from '../../../context/HNContext'
import EmptyState from '../../shared/ui/EmptyState'

interface IPropTypes {
  changelog?: IChangelog
}

export default function UserChangelogSingle({
  changelog: _changelog,
}: IPropTypes) {
  const t = useTranslations()
  const router = useRouter()
  const { changelogSlug } = router.query as {
    changelogSlug: string
  }
  const { embedType, organizationSetting, organization } = useContext(HNContext)

  const [changelogData, setChangelogData] = useState(_changelog)
  const [loading, setLoading] = useState(!_changelog)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const fetchChangelog = () => {
    if (!changelogSlug) {
      setLoading(false)
      return null
    }
    setLoading(true)
    return getChangelogSingle(changelogSlug?.toString())
      .then((newChangelog) => {
        setChangelogData(newChangelog)
      })
      .catch((err) => setErrorMessage(err.message))
      .finally(() => setLoading(false))
  }

  const renderLinkedPosts = () => {
    if (!changelogData?.feature_requests?.length) return null
    return (
      <div
        data-testid='user_changelog_single_linked_posts'
        className='space-y-4 py-4'
      >
        <LineHeader
          color='default'
          align='start'
          title={t('changelog.changelogItem.labels.linkedPosts')}
        />
        {changelogData?.feature_requests.map((post) => (
          <UserPostItem
            key={post.id}
            post={post}
            openInNewTab={embedType === 'changelogWidget'}
          />
        ))}
      </div>
    )
  }

  useEffect(() => {
    if (!changelogData) {
      fetchChangelog()
    }
  }, [changelogSlug])

  const renderContributors = () => {
    if (!changelogData?.contributors?.length) return null
    return (
      <div className=''>
        <LineHeader
          className='!rounded'
          align='center'
          title={t('changelog.changelogItem.labels.credits')}
        />
        <div
          data-testid='user_changelog_single_contributors'
          className='mt-6 flex flex-wrap items-center justify-center gap-2 overflow-hidden'
        >
          {changelogData.contributors.map((contributor: IUserProfile) => (
            <Tooltip key={contributor.id} text={contributor.name}>
              <UserAvatar key={contributor.id} user={contributor} rounded />
            </Tooltip>
          ))}
        </div>
      </div>
    )
  }

  const renderContent = () => {
    if (loading)
      return (
        <div className='md:col-span-6'>
          <ChangelogLoader data-testid='user_changelog_single_spinner' />
        </div>
      )
    if (errorMessage)
      return (
        <EmptyState
          dataTestId='user_side_changelog_single_error_empty_state'
          title={errorMessage}
        />
      )
    if (!changelogData) return null
    return (
      <div className={clsx('flex h-max flex-col gap-5 px-2 md:col-span-6')}>
        <PageTitle
          title={[
            changelogData?.title,
            organizationSetting?.changelog_singular_name,
          ]
            .join(' | ')
            .toString()}
        />
        {!changelogData.published_at && (
          <p className='mb-3 text-xs font-bold uppercase tracking-wide text-primary'>
            {t('common.labels.preview')}
          </p>
        )}

        <div className='grid grid-cols-1 md:grid-cols-12'>
          <div className='col-span-2 mt-2 px-4 py-2 md:px-0 md:py-5'>
            {changelogData.published_at && (
              <Tooltip
                text={formatJSONTimestamp(changelogData.published_at.timestamp)}
              >
                <span className='my-0 flex text-left font-medium'>
                  {relativeDate(changelogData.published_at.timestamp)}
                </span>
              </Tooltip>
            )}
          </div>
          <div className='col-span-10 space-y-3 p-4'>
            <div className='flex items-center justify-between'>
              <div className='flex flex-row flex-wrap items-center space-x-1'>
                <UserChangelogLabels labels={changelogData?.labels} />
              </div>
            </div>
            <h1 className='text-xl font-semibold'>{changelogData.title}</h1>
            {changelogData.feature_image_url && (
              <Image
                width={1000}
                height={1000}
                data-testid='user_changelog_single_cover_image'
                src={changelogData.feature_image_url}
                alt={changelogData.title}
                className='rounded'
              />
            )}

            <article>
              <HTMLViewer
                dataTestId='user_changelog_single_description'
                html={changelogData.description_html}
                className='py-1 transition duration-200 md:py-4'
              />
            </article>
            <div className='hn-animate-duration mt-5 items-center justify-center space-y-4 sm:flex sm:justify-between sm:space-y-0'>
              <ChangelogReactions
                dataTestId='user_changelog_single_reactions'
                changelog={changelogData}
              />
              <ChangelogLinks
                dataTestId='user_changelog_single_links'
                changelog={changelogData}
              />
            </div>
            {renderContributors()}
            {renderLinkedPosts()}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {router.query?.labels && organization && organization.home_page && (
        <NextSeo
          canonical={userSideChangelogSinglePath(
            changelogSlug,
            organization?.home_page
          )}
        />
      )}
      <section data-testid='user_side_changelog_list' className='py-2 md:py-5'>
        <div className='mx-auto flex max-w-5xl flex-col gap-5 px-4'>
          {renderContent()}
        </div>
      </section>
    </>
  )
}
